import { HorizontalNoteContainer } from "../model/containers";
import {
    MetronomeMarking,
    TimeSignature
} from "../model/core";
import { RydProject } from "../model/project";
import {
    playbackConfStore
} from "../stores/PlaybackConfStore";
import { KeyBindingHandlerDefinition } from "../keybindings/dispatcher";
import { CaretController } from "../utils/ScrollController";
import {
    playTracks,
    RydPlayerSession,
    TempoChangeEvent
} from "./AudioController";
import { assert } from "../utils/error";
import { LoopPlaybackSetup } from "../model/loop";
import { workspaceStore } from "../stores/WorkspaceStore";

const getTempoChangeEvents = (): TempoChangeEvent[] => workspaceStore
    .getState().contextPlane.stateObject.generateContextFrames()
    .map(cf => ({
        start: cf.start,
        end: cf.end,
        computed: cf.compute()
    }))
    .map(ccf => ({
        position: ccf.start,
        bpm: ccf.computed.metronome.bpm
    } as TempoChangeEvent));

export const setupPlayerSession = ({
    project,
    timeSignature,
    // metronome,
    caretController,
    loopPlaybackSetup,
    recording
}: {
    project: RydProject,
    timeSignature: TimeSignature,
    // metronome: MetronomeMarking,
    caretController?: CaretController,
    loopPlaybackSetup: LoopPlaybackSetup,
    recording?: boolean
}): RydPlayerSession | undefined => {
    const player = playTracks(
        // FIXME: are we aligned on the track ids and hnc indices?
        project.getAll().map((h, i) => {
            return {
                track: project.tracks.get(i)!,
                instrument: project.instrumentAssignments.get(i),
                container: h as HorizontalNoteContainer
            }
        }),
        timeSignature,
        getTempoChangeEvents(),
        caretController,
        {
            loopPlaybackSetup,
            recording: !!recording
        }
    );

    return player;
};

export function buildKeybindingHandlers(
    project: RydProject,
    caretController: CaretController
): {
    playPause: KeyBindingHandlerDefinition<never>,
    record: KeyBindingHandlerDefinition<never>,
} {
    // FIXME: this is duplicated wrt GlobalPlaybackControls. Both should use the same code.
    return {
        playPause: {
            description: 'Play / Pause',
            handler: (project: RydProject) => {
                console.log('GLOBAL PLAY/PAUSE', playbackConfStore.getState());
                assert(project, 'No project in global play/pause handler');

                if (playbackConfStore.getState().isPlaying && !playbackConfStore.getState().isPaused) {
                    playbackConfStore.getState().playerSession?.pause();
                    playbackConfStore.setState({ isPaused: true });
                } else {
                    const player = setupPlayerSession({
                        project: project,
                        timeSignature: project.contextFrame.computed.timeSignature,
                        // metronome: project.contextFrame.computed.metronome,
                        caretController: caretController,
                        loopPlaybackSetup: playbackConfStore.getState().loopPlaybackSetup,
                        recording: false
                    });
                    playbackConfStore.setState({ playerSession: player });
                    playbackConfStore.setState({ isPlaying: true });
                    playbackConfStore.setState({ isPaused: false });
                    player?.start();
                }
            },
            scopeUnit: 'global',
            trigger: {
                key: ' ',
                ctrl: true
            }
        },
        record: {
            description: 'Record',
            handler: (project: RydProject) => {
                console.log('GLOBAL RECORD', playbackConfStore.getState());
                assert(project, 'No project in global play/pause handler');

                if (playbackConfStore.getState().isPlaying && !playbackConfStore.getState().isPaused) {
                    playbackConfStore.getState().playerSession?.pause();
                    playbackConfStore.setState({ isPaused: true });
                } else {
                    const player = setupPlayerSession({
                        project: project,
                        timeSignature: project.contextFrame.computed.timeSignature,
                        // metronome: project.contextFrame.computed.metronome,
                        caretController: caretController,
                        loopPlaybackSetup: playbackConfStore.getState().loopPlaybackSetup,
                        recording: true
                    });
                    playbackConfStore.setState({ playerSession: player });
                    playbackConfStore.setState({ isPlaying: true });
                    playbackConfStore.setState({ isRecording: true });
                    playbackConfStore.setState({ isPaused: false });
                    player?.start();
                }
            },
            scopeUnit: 'global',
            trigger: {
                key: ' ',
                shift: true
            }
        }
    };
}